import * as z from "zod";
import { V2AdminProductIdSchema } from "./admin-products";
import {
  onboardingV2CustomRangeSchema,
  onboardingV2TimePeriodSchema,
  requireCustomRangeWhenCustom,
} from "./analytics-time-period";

export const onboardingV2StatusSchema = z.enum([
  "partial-completion",
  "completed",
  "help-requested",
]);

export type OnboardingV2Status = z.infer<typeof onboardingV2StatusSchema>;

export const onboardingV2RowSchema = z.object({
  id: z.string(),
  teamId: z.string(),
  productId: z.string(),
  version: z.number(),
  status: onboardingV2StatusSchema,
  currentStepId: z.string(),
  dealSize: z.number().nullable(),
  analyticsSessionId: z.string().nullable(),
  startedAt: z.string(),
  updatedAt: z.string(),
  /**
   * Most recent moment the merchant actually engaged with the onboarding
   * flow. Derived server-side from step_timestamps + started_at +
   * completed_at — does NOT bump on sync writes the way `updatedAt` does.
   * Prefer this over `updatedAt` for any "last activity" display/filter.
   */
  lastActivityAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  archivedByUserId: z.string().nullable(),
  startedByUserId: z.string().nullable(),
  lastUpdatedByUserId: z.string().nullable(),
  stepsCompleted: z.number(),
  totalSteps: z.number(),
  totalTimeMs: z.number(),
  teamName: z.string(),
  teamDomain: z.string().nullable(),
  hubspotDealId: z.string().nullable(),
});

export type OnboardingV2Row = z.infer<typeof onboardingV2RowSchema>;

export const getOnboardingsV2 = {
  name: "getOnboardingsV2" as const,
  input: z
    .object({
      limit: z.number().min(1).max(100).default(20),
      offset: z.number().min(0).default(0),
      productId: z.string().optional(),
      /**
       * Scope the query to a set of admin-visible product IDs. When the
       * "All" tab is active, the list page passes V2_ADMIN_PRODUCT_IDS here
       * so non-admin products (e.g. chargebacks, shipping-fulfillment)
       * don't leak into the result set. `productId` (single-value) takes
       * precedence over `productIds` when both are given.
       */
      productIds: z.array(V2AdminProductIdSchema).optional(),
      statuses: z.array(onboardingV2StatusSchema).optional(),
      searchQuery: z.string().optional(),
      hideArchived: z.boolean().default(true),
      timePeriod: onboardingV2TimePeriodSchema,
      customRange: onboardingV2CustomRangeSchema.optional(),
      sortBy: z
        .enum(["last_activity_at", "updated_at", "started_at", "deal_size"])
        .optional(),
      sortOrder: z.enum(["asc", "desc"]).optional(),
    })
    .superRefine(requireCustomRangeWhenCustom),
  output: z.object({ rows: z.array(onboardingV2RowSchema), total: z.number() }),
};

export type GetOnboardingsV2Input = z.infer<typeof getOnboardingsV2.input>;
export type GetOnboardingsV2Output = z.infer<typeof getOnboardingsV2.output>;
